import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { copyToClipboard } from "../../utils/clipboard";
import { useAuth } from "../../context/AuthContext";
import { uploadToCloudinary } from "../../lib/cloudinary";
import {
  PlusCircle,
  Search,
  Building2,
  ExternalLink,
  MoreVertical,
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Mail,
  RefreshCw,
  Users,
  Send,
  Trash2,
  Power,
  PowerOff,
  UploadCloud,
  Loader2,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientManagement = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [accessStats, setAccessStats] = useState({
    total: 0,
    has_access: 0,
    pending: 0,
    no_access: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAccess, setFilterAccess] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "",
    industry: "",
    linkedin_page_url: "",
    brand_voice: "",
    content_pillars: "",
    tone_preferences: "",
    icp_description: "",
    goal: "",
    logo_url: "",
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await uploadToCloudinary(file);
      setNewClient((prev) => ({ ...prev, logo_url: url }));
    } catch (err) {
      toast.error(err.message || "Failed to upload image");
    } finally {
      setUploadingLogo(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const EMPTY_STATS = { total: 0, has_access: 0, pending: 0, no_access: 0 };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/with-access-status`);
      setClients(response.data.clients || []);
      // Accept both 'stats' (NestJS) and 'counts' (legacy) keys; fall back to zeros
      setAccessStats(
        response.data.stats || response.data.counts || EMPTY_STATS,
      );
    } catch (error) {
      // Fallback to regular clients endpoint
      try {
        const response = await axios.get(`${API_URL}/clients`);
        setClients(response.data.clients || []);
      } catch (err) {
        toast.error("Failed to fetch clients");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!selectedClient || !inviteEmail) return;
    setInviting(true);

    try {
      const response = await axios.post(
        `${API_URL}/clients/${selectedClient.id}/invite?email=${encodeURIComponent(inviteEmail)}`,
      );
      toast.success("Invite sent successfully!");

      // Copy invite link
      const result = await copyToClipboard(response.data.invite_link);
      if (result.success) {
        toast.info("Invite link copied to clipboard");
      }

      setInviteDialogOpen(false);
      setInviteEmail("");
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (client) => {
    try {
      const response = await axios.post(
        `${API_URL}/clients/${client.id}/resend-invite`,
      );
      toast.success("Invite resent to " + response.data.email);

      // Copy invite link
      const result = await copyToClipboard(response.data.invite_link);
      if (result.success) {
        toast.info("Invite link copied to clipboard");
      }

      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invite");
    }
  };

  const handleDeleteClient = async (client) => {
    try {
      await axios.delete(`${API_URL}/clients/${client.id}`);
      setClients(clients.filter((c) => c.id !== client.id));
      toast.success(`Client "${client.name}" deleted successfully`);
      fetchClients(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete client");
    }
  };

  const handleToggleClientStatus = async (client) => {
    const newStatus = client.status === "active" ? "inactive" : "active";
    try {
      await axios.patch(
        `${API_URL}/clients/${client.id}/status?status=${newStatus}`,
      );
      setClients(
        clients.map((c) =>
          c.id === client.id ? { ...c, status: newStatus } : c,
        ),
      );
      toast.success(
        `Client "${client.name}" ${newStatus === "active" ? "activated" : "deactivated"}`,
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to update client status",
      );
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const payload = {
        ...newClient,
        content_pillars: newClient.content_pillars
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };

      const response = await axios.post(`${API_URL}/clients`, payload);
      setClients([response.data.client, ...clients]);
      setIsDialogOpen(false);
      setNewClient({
        name: "",
        industry: "",
        linkedin_page_url: "",
        brand_voice: "",
        content_pillars: "",
        tone_preferences: "",
        icp_description: "",
        goal: "",
        logo_url: "",
      });
      toast.success("Client created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    } finally {
      setCreating(false);
    }
  };

  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.industry?.toLowerCase().includes(searchQuery.toLowerCase());

    if (filterAccess === "all") return matchesSearch;
    return matchesSearch && client.access_status === filterAccess;
  });

  // Access status badge renderer
  const getAccessBadge = (client) => {
    // First check if client is inactive
    if (client.status === "inactive") {
      return (
        <Badge className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400">
          <PowerOff className="w-3 h-3 mr-1" />
          Inactive
        </Badge>
      );
    }

    const status = client.access_status;
    if (status === "active") {
      return (
        <Badge className="bg-[#d8e5b8] text-[#3d4f22] border-[#b8cf88]">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </Badge>
      );
    } else if (status === "pending") {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-slate-100 text-slate-600 border-slate-200">
          <AlertCircle className="w-3 h-3 mr-1" />
          No Access
        </Badge>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Clients
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage your client accounts and portal access
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
              data-testid="add-client-btn"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
              {/* Profile image */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-semibold text-xl shrink-0">
                  {newClient.logo_url ? (
                    <img
                      src={newClient.logo_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    newClient.name?.charAt(0)?.toUpperCase() || "C"
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Label>Profile Image</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <label
                      className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 ${
                        uploadingLogo
                          ? "opacity-70 cursor-wait"
                          : "cursor-pointer hover:border-[#556B2F]"
                      }`}
                      data-testid="client-logo-upload"
                    >
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                        data-testid="client-logo-input"
                      />
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-[#556B2F]" />
                          Uploading…
                        </>
                      ) : (
                        <>
                          <UploadCloud className="w-4 h-4 text-[#556B2F]" />
                          {newClient.logo_url ? "Change" : "Upload"}
                        </>
                      )}
                    </label>
                    {newClient.logo_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setNewClient({ ...newClient, logo_url: "" })}
                      >
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={newClient.name}
                  onChange={(e) =>
                    setNewClient({ ...newClient, name: e.target.value })
                  }
                  placeholder="Acme Corp"
                  required
                  data-testid="client-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Input
                  id="industry"
                  value={newClient.industry}
                  onChange={(e) =>
                    setNewClient({ ...newClient, industry: e.target.value })
                  }
                  placeholder="Technology, Healthcare, etc."
                  data-testid="client-industry-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedin">LinkedIn Page URL</Label>
                <Input
                  id="linkedin"
                  value={newClient.linkedin_page_url}
                  onChange={(e) =>
                    setNewClient({
                      ...newClient,
                      linkedin_page_url: e.target.value,
                    })
                  }
                  placeholder="https://linkedin.com/company/..."
                  data-testid="client-linkedin-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="brand_voice">Brand Voice</Label>
                <Textarea
                  id="brand_voice"
                  value={newClient.brand_voice}
                  onChange={(e) =>
                    setNewClient({ ...newClient, brand_voice: e.target.value })
                  }
                  placeholder="Professional, innovative, friendly..."
                  rows={2}
                  data-testid="client-brandvoice-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content_pillars">
                  Content Pillars (comma separated)
                </Label>
                <Input
                  id="content_pillars"
                  value={newClient.content_pillars}
                  onChange={(e) =>
                    setNewClient({
                      ...newClient,
                      content_pillars: e.target.value,
                    })
                  }
                  placeholder="Industry insights, Product updates, Team culture"
                  data-testid="client-pillars-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tone">Tone Preferences</Label>
                <Input
                  id="tone"
                  value={newClient.tone_preferences}
                  onChange={(e) =>
                    setNewClient({
                      ...newClient,
                      tone_preferences: e.target.value,
                    })
                  }
                  placeholder="Approachable yet authoritative"
                  data-testid="client-tone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="icp">Ideal Customer Profile (ICP)</Label>
                <Textarea
                  id="icp"
                  value={newClient.icp_description}
                  onChange={(e) =>
                    setNewClient({
                      ...newClient,
                      icp_description: e.target.value,
                    })
                  }
                  placeholder="CTOs at mid-size tech companies..."
                  rows={2}
                  data-testid="client-icp-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="goal">Content Goal</Label>
                <Textarea
                  id="goal"
                  value={newClient.goal}
                  onChange={(e) =>
                    setNewClient({ ...newClient, goal: e.target.value })
                  }
                  placeholder="Build thought leadership and generate leads"
                  rows={2}
                  data-testid="client-goal-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-[#556B2F] hover:bg-[#3d4f22] text-white"
                  data-testid="create-client-submit"
                >
                  {creating ? "Creating..." : "Create Client"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Access Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          className={`cursor-pointer transition-all ${filterAccess === "all" ? "ring-2 ring-[#556B2F]" : ""}`}
          onClick={() => setFilterAccess("all")}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="w-8 h-8 text-[#556B2F]" />
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {accessStats.total}
              </p>
              <p className="text-xs text-slate-500">Total Clients</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filterAccess === "active" ? "ring-2 ring-[#556B2F]" : ""}`}
          onClick={() => setFilterAccess("active")}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-[#556B2F]" />
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {accessStats.has_access}
              </p>
              <p className="text-xs text-slate-500">Has Access</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filterAccess === "pending" ? "ring-2 ring-amber-500" : ""}`}
          onClick={() => setFilterAccess("pending")}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {accessStats.pending}
              </p>
              <p className="text-xs text-slate-500">Pending Setup</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filterAccess === "no_access" ? "ring-2 ring-slate-400" : ""}`}
          onClick={() => setFilterAccess("no_access")}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-slate-400" />
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {accessStats.no_access}
              </p>
              <p className="text-xs text-slate-500">No Access</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="client-search-input"
        />
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 dark:text-white mb-2">
              {searchQuery ? "No clients found" : "No clients yet"}
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              {searchQuery
                ? "Try a different search term"
                : "Add your first client to get started"}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <Card
              key={client.id}
              className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-[#b8cf88] dark:hover:border-[#3d4f22] transition-all"
              data-testid={`client-card-${client.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div
                    className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() => navigate(`/agency/clients/${client.id}`)}
                  >
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-semibold text-lg shrink-0">
                      {client.logo_url ? (
                        <img
                          src={client.logo_url}
                          alt={client.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        client.name?.charAt(0)?.toUpperCase() || "C"
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">
                        {client.name}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {client.industry || "No industry"}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      asChild
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/agency/clients/${client.id}`);
                        }}
                      >
                        View Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/agency/calendar/${client.id}`);
                        }}
                      >
                        View Calendar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/agency/posts/new?client=${client.id}`);
                        }}
                      >
                        Create Post
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {client.status !== "inactive" && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedClient(client);
                            setInviteDialogOpen(true);
                          }}
                          className="text-[#3d4f22] dark:text-[#b8cf88]"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          {client.access_status === "pending"
                            ? "Resend Invite"
                            : "Send Portal Access"}
                        </DropdownMenuItem>
                      )}
                      {isSuperAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleClientStatus(client);
                            }}
                            className={
                              client.status === "inactive"
                                ? "text-[#3d4f22] dark:text-[#b8cf88]"
                                : "text-amber-600 dark:text-amber-400"
                            }
                          >
                            {client.status === "inactive" ? (
                              <>
                                <Power className="w-4 h-4 mr-2" /> Activate
                              </>
                            ) : (
                              <>
                                <PowerOff className="w-4 h-4 mr-2" /> Deactivate
                              </>
                            )}
                          </DropdownMenuItem>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Client
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Delete Client?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete{" "}
                                  <strong>{client.name}</strong> and all
                                  associated posts, comments, and data. This
                                  action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteClient(client)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Access Status */}
                <div className="flex items-center justify-between mb-3">
                  {getAccessBadge(client)}
                  {client.access_email && (
                    <span className="text-xs text-slate-500 truncate max-w-[150px]">
                      {client.access_email}
                    </span>
                  )}
                </div>

                {/* Access Actions - Always show invite option */}
                {(client.access_status === "no_access" ||
                  !client.access_status) &&
                  client.status !== "inactive" && (
                    <div className="mb-3">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedClient(client);
                          setInviteDialogOpen(true);
                        }}
                        className="w-full bg-[#3d4f22] hover:bg-[#3d4f22]"
                        data-testid={`send-access-${client.id}`}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send Portal Access
                      </Button>
                    </div>
                  )}

                {client.access_status === "pending" && (
                  <div className="mb-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResendInvite(client);
                      }}
                      className="w-full"
                      data-testid={`resend-${client.id}`}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Invite
                    </Button>
                  </div>
                )}

                {client.linkedin_page_url && (
                  <a
                    href={client.linkedin_page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-sm text-[#556B2F] hover:underline mb-3"
                  >
                    <ExternalLink className="w-3 h-3" />
                    LinkedIn Page
                  </a>
                )}

                <div className="flex items-center gap-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <FileText className="w-4 h-4" />
                    <span>{client.stats?.total_posts || 0} posts</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/agency/clients/${client.id}`);
                    }}
                  >
                    View
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Send Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#3d4f22] dark:text-[#b8cf88]" />
              Send Portal Access to {selectedClient?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Client Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="client@company.com"
                data-testid="invite-email-input"
              />
            </div>
            <p className="text-sm text-slate-600">
              The client will receive an email with a link to set up their
              password and access the portal.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setInviteDialogOpen(false);
                  setInviteEmail("");
                  setSelectedClient(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendInvite}
                disabled={inviting || !inviteEmail}
                className="flex-1 bg-[#3d4f22] hover:bg-[#3d4f22]"
                data-testid="send-invite-submit"
              >
                {inviting ? "Sending..." : "Send Invite"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientManagement;
